<template>
	<g :transform="`translate(${(surveyIndex % 2 == 0 ? 30 : 541)} ${surveyBlockMargin.top + previousBlockRowBottom + surveyBlockMargin.bottom})`" fill="#404040" fill-rule="nonzero">
		<!-- Background -->
		<rect width="480" :height="surveyBlockRowHeight" fill="#FFF" rx="16"></rect>

		<!-- Title -->
		<text fill="#000" font-size="18">
			<tspan x="20" y="38">{{ survey.title }}</tspan>
		</text>

		<!-- Questions -->
		<g
			v-for="(question, questionIndex) in survey.questions"
			:key="question.id"
			:class="{ 'mb-4': questionIndex < survey.questions.length - 1 }"
			:transform="`translate(20 ${questionBlockPositionBySurveyIndex[surveyIndex][questionIndex].y})`"
		>
			<text>
				<tspan
					v-for="(line, lineIndex) in pdfTemplate.getLinesForText(question.text, surveyQuestionTextCharLimit)"
					:key="lineIndex"
					x="0"
					:y="15 + (questionTextHeight * lineIndex)"
				>
					{{ line }}
				</tspan>

				<!-- Star average -->
				<tspan
					v-if="question.type.slug == 'star'"
					x="418"
					:y="15 + (questionBlockPositionBySurveyIndex[surveyIndex][questionIndex].questionTextHeight - questionTextHeight)"
					text-anchor="end"
				>
					{{ formatedQuestionAverageResult[question.id] || '-' }}
				</tspan>
			</text>

			<!-- Star icon -->
			<g
				v-if="question.type.slug == 'star'"
				:transform="`translate(418 ${5 + (questionBlockPositionBySurveyIndex[surveyIndex][questionIndex].questionTextHeight - questionTextHeight)})`"
			>
				<path d="M8.944 10.48 7.76 6.56l3.28-2.528H6.992L5.648.096 4.304 4.032H.256l3.28 2.528-1.184 3.92 3.296-2.336z"/>
			</g>

			<!-- Answers -->
			<g
				v-for="(answer, index) in question.answers"
				:key="answer.id"
				:transform="`translate(0 ${questionBlockPositionBySurveyIndex[surveyIndex][questionIndex].answerPositionByIndex[index].y})})`"
			>
				<rect width="20" height="20" fill="#006FB7"></rect>
				<text fill="#FFF">
					<tspan x="5.164" y="16">{{ getAnswerIndexLabel(index) }}</tspan>
				</text>

				<text>
					<tspan
						v-for="(line, lineIndex) in pdfTemplate.getLinesForText(answer.text, surveyAnswerCharLimit)"
						:key="lineIndex"
						x="28"
						:y="15 + (answerHeight * lineIndex)"
					>
						{{ line }}
					</tspan>
				</text>

				<text>
					<tspan x="430.75" y="15" text-anchor="end">{{ formatedAnswersResult[answer.id + question.id] || "-" }}</tspan>
				</text>
			</g>

			<!-- Number of answers for this question -->
			<text
				v-if="survey.results.questionCount[question.id]"
				font-size="14"
			>
				<tspan
					x="370.731"
					:y="16 + questionBlockPositionBySurveyIndex[surveyIndex][questionIndex].questionTextHeight + questionTextMarginBottom + questionBlockPositionBySurveyIndex[surveyIndex][questionIndex].answersHeight"
				>
					{{ survey.results.questionCount[question.id] }} réponses
				</tspan>
			</text>
		</g>
	</g>
</template>

<script>
import pdfTemplate from '@/utils/pdfTemplate'

function formatToPercentage(firstValue, secondValue) {
	const value = ((firstValue || 0) / (secondValue || 1))

	return pdfTemplate.formatValue(value, 'percentage')
}

export default {
	name: 'SurveyResults',
	props: {
		survey: {
			type: Object,
			required: true,
		},
		surveyIndex: {
			type: Number,
			required: true,
		},
		questionBlockPositionBySurveyIndex: {
			type: Array,
			required: true,
		},
		surveyBlockHeightByIndex: {
			type: Array,
			required: true,
		},
		questionTextHeight: {
			type: Number,
			required: true,
		},
		questionTextMarginBottom: {
			type: Number,
			required: true,
		},
		answerHeight: {
			type: Number,
			required: true,
		},
		surveyQuestionTextCharLimit: {
			type: Number,
			required: true,
		},
		surveyAnswerCharLimit: {
			type: Number,
			required: true,
		},
	},
	data() {
		return {
			pdfTemplate,
			surveyBlockMargin: {
				top: 65,
				bottom: 32,
			},
		}
	},
	computed: {
		surveyBlockRowHeight() {
			// Current row max height
			return Math.max(this.surveyBlockHeightByIndex[this.surveyIndex], this.surveyBlockHeightByIndex[this.surveyIndex + (this.surveyIndex % 2 == 0 ? 1 : -1)] || 0)
		},
		previousBlockRowBottom() {
			let bottom = 0

			if (this.surveyIndex > 1) {
				for (var i = 0; i < this.surveyIndex - 1; i += 2) {
					const firstBlockHeight = this.surveyBlockHeightByIndex[i]
					const secondBlockHeight = this.surveyBlockHeightByIndex[i + 1] || 0

					bottom += Math.max(firstBlockHeight, secondBlockHeight) + this.surveyBlockMargin.bottom
				}
			}

			return bottom
		},
		formatedAnswersResult() {
			return this.survey.questions.reduce((dict, question) => {
				const questionCount = this.survey.results.questionCount[question.id];

				question.answers.forEach((answer) => {
					const answerCount =
					this.survey.results.answerCount[answer.id + question.id] || 0;

					if (!questionCount) {
						dict[answer.id + question.id] = "-";
					} else {
						dict[answer.id + question.id] = formatToPercentage(
							answerCount,
							questionCount
						);
					}
				});

				return dict;
			}, {});
		},
		formatedQuestionAverageResult() {
			return this.survey.questions.reduce((dict, question) => {
				const questionCount = this.survey.results.questionCount[question.id];

				if (questionCount <= 0 || question.type.slug != 'star') {
					return dict;
				}

				const answersList = this.survey.results.answers[question.id]

				if (!answersList) {
					return dict;
				}

				const total = answersList.reduce((total, answers) => {
					return total + parseInt(answers[0], 10);
				}, 0);

				dict[question.id] = (total / questionCount).toFixed(2);

				return dict;
			}, {});
		},
	},
	methods: {
		getAnswerIndexLabel(index) {
			let label = "";

			const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
			for (let i = 0; i < Math.ceil((index + 1) / letters.length); i += 1) {
				label += letters[index % letters.length];
			}

			return label;
		},
	},
}
</script>